import React from 'react';
import { CheckCircle, Award, GraduationCap, Briefcase, Globe, Wrench } from 'lucide-react';

// Founder image
const FOUNDER_IMAGE = 'https://customer-assets.emergentagent.com/job_3838beab-b8f6-4a74-ab9c-617e0dffa901/artifacts/3ghacy77_IMG_2441.png';

const technicalQualifications = [
  "Ausgebildeter Elektriker",
  "Staatlich geprüfter Elektrotechniker",
  "Staatlich geprüfter Wirtschaftstechniker",
  "Zertifizierter Kälte- und Klimatechniker"
];

const industryExperience = [
  "Produktentwicklung von Photovoltaikmodulen",
  "Qualitätsmanagement für PV-Module",
  "Reklamationsmanagement für Photovoltaikanlagen",
  "Produktmanagement für Wechselrichter und DC-Technik"
];

const projectExperience = [
  "Über 10 MW installierte Leistung",
  "Erfahrung mit Freiflächenanlagen und Großanlagen",
  "Spezialisierung auf Residential-Anlagen",
  "Ein- und Mehrfamilienhausbereich"
];

const FounderSection = () => {
  return (
    <section id="ueber-uns" data-testid="founder-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-sun-orange/10 text-sun-orange rounded-full text-sm font-semibold mb-4">
            Expertise
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            20 Jahre Erfahrung in der
            <span className="text-sun-orange"> Photovoltaikbranche</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Tiefgreifende Branchenerfahrung von der Modulentwicklung über Qualitätsmanagement 
            bis zur Projektrealisierung – für Ihr optimales Energiesystem.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Left - Founder Image */}
          <div className="lg:col-span-2 relative">
            <div className="aspect-[3/4] bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={FOUNDER_IMAGE} 
                alt="Michael Kaufmann - Gründer & Geschäftsführer"
                className="w-full h-full object-cover object-top"
              />
              
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
              
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-2xl font-bold text-white">Michael Kaufmann</div>
                <div className="text-white/70">Gründer & Geschäftsführer</div>
              </div>
            </div>
            
            {/* Experience Badge */}
            <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-white rounded-2xl p-5 shadow-xl border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">20+</div>
                  <div className="text-slate-500 text-sm">Jahre Erfahrung</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Technical Qualifications */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-sun-blue/10 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-sun-blue" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Technische Qualifikation</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {technicalQualifications.map((qual, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-sun-blue flex-shrink-0" />
                    <span className="text-slate-700">{qual}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Industry Experience */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-sun-orange/10 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-sun-orange" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Branchenerfahrung</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {industryExperience.map((exp, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-sun-orange flex-shrink-0" />
                    <span className="text-slate-700">{exp}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* International Experience */}
            <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <Globe className="w-6 h-6 text-sun-orange" />
                </div>
                <h3 className="text-xl font-bold">Internationale Projekterfahrung</h3>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                Mehrjährige internationale Erfahrung in der Planung und Umsetzung von Photovoltaikprojekten. 
                Unter anderem mehrere Jahre in Belgien bei der Realisierung von Großanlagen und 
                Freiflächenanlagen tätig.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-sun-orange">10 MW+</div>
                  <div className="text-white/60 text-sm">Installierte Leistung</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-sun-orange">500+</div>
                  <div className="text-white/60 text-sm">Projekte begleitet</div>
                </div>
              </div>
            </div>

            {/* Project Experience */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Projekterfahrung</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {projectExperience.map((proj, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{proj}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;
